import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  ImageGeneratorCatalogSurfaceSlide,
  resolveImageGeneratorResultSrc,
} from "../../app/api/imageGenerator";
import {
  extractGenerativePalette,
  getGenerativeApiErrorMessage,
  mapExtractedPalette,
  PALETTE_EXTRACT_ACCEPT,
  paletteExtractFileError,
} from "./shared/generativeCatalog";
import CollectionBuildColorPicker from "./CollectionBuildColorPicker";
import CollectionBuildPreferences from "./CollectionBuildPreferences";
import CollectionBuildReportSlider, {
  CollectionBuildReportSlide,
} from "./CollectionBuildReportSlider";
import CollectionBuildSelect from "./CollectionBuildSelect";
import {
  ColorPaletteOption,
  ColorPaletteSwatch,
  DENIM_PALETTE_UPLOAD_HELPER,
  DIRECTION_UNAVAILABLE_HINT,
  MICRO_THEME_HELPER,
  MOODBOARD_HELPER,
  PALETTE_UPLOAD_HELPER,
  PATTERN_TREND_CHOICES,
  PATTERN_TREND_HELPER,
  PREFERENCE_SECTION_HELPER,
  SELECT_DIRECTION_HELPER,
  TREND_SOURCE_CHOICES,
  WESTERN_PATTERN_NARROW_HELPER,
  PatternTrendChoice,
  PreferenceGroup,
  SelectOption,
  StartingDirection,
  TrendSourceChoice,
  capsulePaletteDisplayName,
  collectionBuildBorderClassName,
  countCustomPalettes,
  createCustomPalette,
  exclusivePillClassName,
  helperTextClassName,
  microThemeSwitcherPillClassName,
  mutedButtonClassName,
  outlineButtonClassName,
  paletteActionPillClassName,
  paletteChipClassName,
  paletteColumnClassName,
  paletteHasMixedTiers,
  paletteLabelClassName,
  paletteSwatchTitle,
  paletteTrackDotClassName,
  orderPaletteDotsMajorFirst,
  RequiredMark,
  scrollCollectionSectionIntoView,
  sectionBlockClassName,
  sectionHelperClassName,
  sectionTitleClassName,
  trimmedPantoneField,
  textareaClassName,
  formFieldsShellClassName,
} from "./collectionBuildConstants";

const SectionBlock = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode }
>(function SectionBlock({ children }, ref) {
  return (
    <div ref={ref} className={sectionBlockClassName}>
      <div className={formFieldsShellClassName}>{children}</div>
    </div>
  );
});

const ExclusivePills = <T extends string>({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<{
    value: T;
    label: string;
    disabled?: boolean;
    title?: string;
  }>;
  value: T | "";
  onChange: (value: T | "") => void;
}) => (
  <div className="flex min-w-0 max-w-full flex-wrap gap-4">
    {options.map(option => {
      const isActive = value === option.value;
      const isDisabled = Boolean(option.disabled);
      return (
        <span key={option.value} title={option.title} className="inline-flex">
          <button
            type="button"
            disabled={isDisabled}
            aria-disabled={isDisabled}
            onClick={() => {
              if (isDisabled) {
                return;
              }
              onChange(isActive ? "" : option.value);
            }}
            className={exclusivePillClassName(isActive, isDisabled)}
          >
            {option.label}
          </button>
        </span>
      );
    })}
  </div>
);

const toPillOptions = <T extends string>(values: readonly T[]) =>
  values.map(value => ({ value, label: value }));

const useScrollIntoViewOnShow = (
  shown: boolean,
  block: ScrollLogicalPosition = "nearest"
) => {
  const ref = useRef<HTMLDivElement>(null);
  const wasShownRef = useRef(false);
  useEffect(() => {
    if (!shown) {
      wasShownRef.current = false;
      return;
    }
    if (wasShownRef.current) {
      return;
    }
    wasShownRef.current = true;
    const frame = window.requestAnimationFrame(() => {
      scrollCollectionSectionIntoView(ref.current, block);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [shown, block]);
  return ref;
};

const PaletteDots: React.FC<{
  colors: string[];
  idPrefix: string;
  swatches?: ColorPaletteSwatch[];
  /** Catalog capsule theme name. Empty keeps the per-swatch title. */
  dotTitle?: string;
}> = ({ colors, idPrefix, swatches, dotTitle }) => {
  const ordered = orderPaletteDotsMajorFirst(colors, swatches);
  const mixedTiers = paletteHasMixedTiers(ordered.swatches);
  const capsuleTitle = String(dotTitle || "").trim();
  return (
    <>
      {ordered.colors.map((hex, index) => {
        const swatch = ordered.swatches?.[index];
        return (
          <span
            key={`${idPrefix}-${index}`}
            title={capsuleTitle || paletteSwatchTitle(hex, swatch, mixedTiers)}
            className={paletteTrackDotClassName}
            style={{ backgroundColor: hex }}
          />
        );
      })}
    </>
  );
};

/**
 * Multi-select micro-theme capsules. Selected = theme chosen for generate;
 * active = which theme’s optional preferences are shown below.
 */
const MicroThemeSelectPills = <T extends string>({
  options,
  activeValue,
  selectedValues,
  onSelect,
  groupLabel = "Micro-themes",
}: {
  options: ReadonlyArray<{
    value: T;
    label: string;
    disabled?: boolean;
    title?: string;
  }>;
  activeValue: T | "";
  selectedValues: readonly T[];
  onSelect: (value: T) => void;
  groupLabel?: string;
}) => (
  <div
    className="flex min-w-0 max-w-full flex-wrap gap-4"
    role="group"
    aria-label={groupLabel}
  >
    {options.map(option => {
      const isActive = activeValue === option.value;
      const isSelected = selectedValues.includes(option.value);
      const isDisabled = Boolean(option.disabled);
      return (
        <span key={option.value} title={option.title} className="inline-flex">
          <button
            type="button"
            disabled={isDisabled}
            aria-disabled={isDisabled}
            aria-pressed={isSelected}
            onClick={() => {
              if (isDisabled) {
                return;
              }
              onSelect(option.value);
            }}
            className={microThemeSwitcherPillClassName(
              isActive && isSelected,
              isSelected,
              isDisabled
            )}
          >
            {String(option.label || "").trim() || option.value}
          </button>
        </span>
      );
    })}
  </div>
);

const slidePageButtonClassName = `box-border flex h-8 w-8 items-center justify-center rounded-lg border ${collectionBuildBorderClassName} bg-white text-sm text-secondary hover:bg-[#F6F3F0] disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none`;

/** Figma Slide Reports: wide carousel + prev / page / of N / next (CB chrome). */
const CollectionBuildSlideReports: React.FC<{
  slides: ImageGeneratorCatalogSurfaceSlide[];
  selectedIds: string[];
  loading?: boolean;
  onToggle: (id: string) => void;
}> = ({ slides, selectedIds, loading, onToggle }) => {
  const total = slides.length;
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(prev => (total > 0 ? Math.min(Math.max(prev, 1), total) : 1));
  }, [total]);

  const safePage = total > 0 ? Math.min(Math.max(page, 1), total) : 1;
  const current = total > 0 ? slides[safePage - 1] : null;
  const isSelected = Boolean(current && selectedIdSet.has(current.id));
  const rawUrl =
    current?.image?.available && current.image.url ? current.image.url : "";
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;
    setSrc(null);
    if (!rawUrl) {
      return;
    }
    (async () => {
      try {
        const preview = await resolveImageGeneratorResultSrc(rawUrl);
        if (cancelled) {
          if (preview.startsWith("blob:")) {
            URL.revokeObjectURL(preview);
          }
          return;
        }
        if (preview.startsWith("blob:")) {
          blobUrl = preview;
        }
        setSrc(preview);
      } catch {
        if (!cancelled) {
          setSrc(rawUrl);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [rawUrl]);

  if (loading) {
    return (
      <p className={`${helperTextClassName} mt-3`}>Loading slide reports…</p>
    );
  }
  if (total === 0) {
    return (
      <p className={`${helperTextClassName} mt-3`}>
        No surface-report slides for these filters.
      </p>
    );
  }

  return (
    <div className="mt-4 w-full min-w-0">
      <div className="inline-block max-w-full">
        <button
          type="button"
          aria-pressed={isSelected}
          title={current?.name || "Surface slide"}
          onClick={() => {
            if (!current) {
              return;
            }
            onToggle(current.id);
          }}
          className={`relative inline-block max-w-full overflow-hidden rounded-[20px] border p-2.5 leading-none focus:outline-none ${
            isSelected
              ? "border-[#D3C0B6] bg-brandPrimarySubtle"
              : `${collectionBuildBorderClassName} bg-white hover:border-[#D3C0B6] hover:bg-[#F8F4F1]`
          }`}
        >
          <span
            className={`block overflow-hidden rounded-[14px] ${
              isSelected ? "bg-white" : "bg-[#F6F3F0]"
            }`}
          >
            {src ? (
              <img
                src={src}
                alt={current?.name || "Surface slide"}
                className="block h-auto w-auto max-h-[460px] max-w-full object-contain sm:max-h-[560px]"
                draggable={false}
              />
            ) : rawUrl ? (
              <span className="flex h-[240px] w-[min(100%,640px)] items-center justify-center px-4 text-sm text-tertiary sm:h-[300px]">
                Loading…
              </span>
            ) : (
              <span className="flex h-[240px] w-[min(100%,640px)] items-center justify-center px-4 text-sm text-secondary sm:h-[300px]">
                {current?.name || "Slide"}
              </span>
            )}
          </span>
        </button>

        <div className="mt-3 flex items-center justify-end gap-2 text-sm text-secondary">
          <button
            type="button"
            aria-label="Previous slide report"
            disabled={safePage <= 1}
            onClick={() => setPage(Math.max(1, safePage - 1))}
            className={slidePageButtonClassName}
          >
            &lt;
          </button>
          <div className="w-[72px]">
            <CollectionBuildSelect
              value={String(safePage)}
              onChange={next => setPage(Number(next))}
              options={Array.from({ length: total }, (_, index) => ({
                value: String(index + 1),
                label: String(index + 1),
              }))}
              placeholder="1"
              compact
            />
          </div>
          <span>of {total}</span>
          <button
            type="button"
            aria-label="Next slide report"
            disabled={safePage >= total}
            onClick={() => setPage(Math.min(total, safePage + 1))}
            className={slidePageButtonClassName}
          >
            &gt;
          </button>
        </div>
      </div>
    </div>
  );
};

const viewReportButtonClassName = `mt-3 h-9 rounded-lg border ${collectionBuildBorderClassName} bg-white px-4 font-avenir text-sm font-medium text-secondary transition-colors hover:bg-brandPrimarySubtle focus:outline-none`;

type Props = {
  startingDirection: StartingDirection | "";
  macroTheme: string;
  selectedPaletteIds: string[];
  extraPalettes: ColorPaletteOption[];
  catalogPalettes: ColorPaletteOption[];
  macroOptions: SelectOption[];
  microOptions: SelectOption[];
  preferenceGroups: PreferenceGroup[];
  trendSource: TrendSourceChoice | "";
  surfaceSlides: ImageGeneratorCatalogSurfaceSlide[];
  surfacesLoading: boolean;
  selectedSurfaceIds: string[];
  patternChoice: PatternTrendChoice | "";
  activeMicroTheme: string;
  selectedMicroThemes: string[];
  selectedPreferenceIds: string[];
  patternStoryOptions?: SelectOption[];
  /** Active Pattern Story (prefs tiles / last clicked). */
  activePatternStoryId?: string;
  /** All selected Pattern Story ids (multi-select). */
  selectedPatternStoryIds?: string[];
  macroReportSlides?: CollectionBuildReportSlide[];
  /** Ethnic macro / Western selected-micro Pattern Trend report (inline slider). */
  patternTrendReportSlides?: CollectionBuildReportSlide[];
  /**
   * Western: Macro → Micro pills → Pattern Story pills → tiles.
   * Ethnic: Pattern Story pills (no micro step). View report sits on Pattern Trend.
   */
  isWesternPatternFlow?: boolean;
  showDirection: boolean;
  directionOptions: readonly StartingDirection[];
  enabledDirections?: readonly StartingDirection[];
  directionMessage?: string;
  showMacro: boolean;
  themeSectionTitle?: string;
  themeEmptyMessage?: string;
  showPalette: boolean;
  showTrendSource: boolean;
  showSurfaceSlides: boolean;
  showPattern: boolean;
  showMicro: boolean;
  showPreferences: boolean;
  showPatternStories?: boolean;
  showMoodboard: boolean;
  /** Hide generate CTAs while keeping Enter Input visible (queryLocked / IG parity). */
  showGenerateActions?: boolean;
  additionalPrompt: string;
  catalogMessage?: string;
  onStartingDirection: (value: StartingDirection | "") => void;
  onMacroTheme: (value: string) => void;
  onTogglePalette: (id: string) => void;
  onAddPalette: (palette: ColorPaletteOption) => void;
  onTrendSource: (value: TrendSourceChoice | "") => void;
  onToggleSurfaceId: (id: string) => void;
  onPatternChoice: (value: PatternTrendChoice | "") => void;
  onSelectMicroTheme: (value: string) => void;
  onSelectPatternStory?: (value: string) => void;
  onTogglePreference: (id: string) => void;
  onAdditionalPromptChange: (value: string) => void;
  onGenerateMoodboard: () => void;
  onGenerateLooks: () => void;
  afterPreferences?: React.ReactNode;
  actionsLocked?: boolean;
};

const CollectionBuildDirections: React.FC<Props> = ({
  startingDirection,
  macroTheme,
  selectedPaletteIds,
  extraPalettes,
  catalogPalettes,
  macroOptions,
  microOptions,
  preferenceGroups,
  trendSource,
  surfaceSlides,
  surfacesLoading,
  selectedSurfaceIds,
  patternChoice,
  activeMicroTheme,
  selectedMicroThemes,
  selectedPreferenceIds,
  patternStoryOptions = [],
  activePatternStoryId = "",
  selectedPatternStoryIds = [],
  macroReportSlides = [],
  patternTrendReportSlides = [],
  isWesternPatternFlow = false,
  showDirection,
  directionOptions,
  enabledDirections,
  directionMessage,
  showMacro,
  themeSectionTitle = "Select Macro Theme",
  themeEmptyMessage = "No macro themes for these filters yet.",
  showPalette,
  showTrendSource,
  showSurfaceSlides,
  showPattern,
  showMicro,
  showPreferences,
  showPatternStories = false,
  showMoodboard,
  showGenerateActions = true,
  additionalPrompt,
  catalogMessage,
  onStartingDirection,
  onMacroTheme,
  onTogglePalette,
  onAddPalette,
  onTrendSource,
  onToggleSurfaceId,
  onPatternChoice,
  onSelectMicroTheme,
  onSelectPatternStory,
  onTogglePreference,
  onAdditionalPromptChange,
  onGenerateMoodboard,
  onGenerateLooks,
  afterPreferences,
  actionsLocked = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [paletteExtracting, setPaletteExtracting] = useState(false);
  const [showMacroReport, setShowMacroReport] = useState(false);
  const [showPatternTrendReport, setShowPatternTrendReport] = useState(false);
  const directionRef = useScrollIntoViewOnShow(showDirection, "nearest");
  const macroRef = useScrollIntoViewOnShow(showMacro, "start");
  const paletteRef = useScrollIntoViewOnShow(showPalette, "start");
  const trendSourceRef = useScrollIntoViewOnShow(showTrendSource, "start");
  const surfaceRef = useScrollIntoViewOnShow(showSurfaceSlides, "start");
  const patternRef = useScrollIntoViewOnShow(showPattern, "start");
  const microRef = useScrollIntoViewOnShow(showMicro, "start");
  const preferencesRef = useScrollIntoViewOnShow(showPreferences, "start");
  const moodboardRef = useScrollIntoViewOnShow(showMoodboard, "start");
  const palettes = [...catalogPalettes, ...extraPalettes];
  const isDenimPath = startingDirection === "Denim";
  const hasPaletteSelected = selectedPaletteIds.length > 0;
  const catalogPaletteIds = new Set(catalogPalettes.map(palette => palette.id));
  const capsuleThemeName =
    startingDirection === "Capsules"
      ? String(
          macroOptions.find(option => option.value === macroTheme)?.label || ""
        ).trim()
      : "";

  useEffect(() => {
    setShowMacroReport(false);
  }, [macroTheme]);

  useEffect(() => {
    setShowPatternTrendReport(false);
  }, [
    macroTheme,
    patternChoice,
    activeMicroTheme,
    selectedMicroThemes,
    isWesternPatternFlow,
  ]);

  const handleSaveCustom = (colors: string[]) => {
    onAddPalette(
      createCustomPalette(colors, countCustomPalettes(extraPalettes))
    );
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    const fileError = paletteExtractFileError(file);
    if (fileError) {
      toast.error(fileError);
      return;
    }
    setPaletteExtracting(true);
    try {
      const mapped = mapExtractedPalette(await extractGenerativePalette(file));
      if (!mapped.hexes.length) {
        toast.error("No fashion colours were found in that image.");
        return;
      }
      onAddPalette({
        id: `upload-${Date.now()}`,
        label: mapped.title,
        colors: mapped.hexes,
        swatches: mapped.swatches.map(item => ({
          hex: item.hex,
          name: item.name || undefined,
          tier: item.tier,
          ...trimmedPantoneField(item.pantone),
        })),
      });
      toast.success("Palette extracted from image.");
    } catch (error) {
      toast.error(
        getGenerativeApiErrorMessage(
          error,
          "Unable to extract a palette from that image."
        )
      );
    } finally {
      setPaletteExtracting(false);
    }
  };

  const uploadControl = (
    <>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={paletteExtracting}
        className={`${paletteActionPillClassName} disabled:cursor-wait disabled:opacity-60`}
      >
        {paletteExtracting ? "Extracting..." : "Upload"}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={PALETTE_EXTRACT_ACCEPT}
        className="hidden"
        onChange={handleUpload}
      />
    </>
  );

  return (
    <>
      {showDirection && (
        <SectionBlock ref={directionRef}>
          <h2 className={sectionTitleClassName}>
            Starting Direction
            <RequiredMark />
          </h2>
          <p className={sectionHelperClassName}>{SELECT_DIRECTION_HELPER}</p>
          <ExclusivePills
            options={toPillOptions(directionOptions).map(option => {
              const disabled = enabledDirections
                ? !enabledDirections.includes(option.value)
                : false;
              return {
                ...option,
                disabled,
                title: disabled ? DIRECTION_UNAVAILABLE_HINT : undefined,
              };
            })}
            value={startingDirection}
            onChange={onStartingDirection}
          />
          {directionMessage ? (
            <p className={`${helperTextClassName} mt-3`}>{directionMessage}</p>
          ) : null}
        </SectionBlock>
      )}

      {showMacro && (
        <SectionBlock ref={macroRef}>
          <h2 className={`${sectionTitleClassName} mb-3`}>
            {themeSectionTitle}
            <RequiredMark />
          </h2>
          <ExclusivePills
            options={macroOptions}
            value={macroTheme}
            onChange={onMacroTheme}
          />
          {!macroOptions.length && !catalogMessage ? (
            <p className={`${helperTextClassName} mt-3`}>{themeEmptyMessage}</p>
          ) : null}
          {catalogMessage ? (
            <p className={`${helperTextClassName} mt-3`}>{catalogMessage}</p>
          ) : null}
          {macroTheme && macroReportSlides.length > 0 ? (
            <>
              <button
                type="button"
                aria-expanded={showMacroReport}
                onClick={() => setShowMacroReport(open => !open)}
                className={viewReportButtonClassName}
              >
                {showMacroReport ? "Hide report" : "View report"}
              </button>
              {showMacroReport ? (
                <CollectionBuildReportSlider slides={macroReportSlides} />
              ) : null}
            </>
          ) : null}
        </SectionBlock>
      )}

      {showPalette && (
        <SectionBlock ref={paletteRef}>
          <h2 className={`${sectionTitleClassName} mb-3`}>
            Select Color Palette
            <RequiredMark />
          </h2>
          {isDenimPath ? (
            <>
              <div className="flex w-fit max-w-full flex-wrap items-start justify-start gap-x-8 gap-y-4">
                <div className="min-w-0 w-fit max-w-full">
                  <div className="flex min-w-0 max-w-full flex-wrap items-start gap-x-4 gap-y-3">
                    {catalogPalettes.map(palette => {
                      const isActive = selectedPaletteIds.includes(palette.id);
                      const isMuted = hasPaletteSelected && !isActive;
                      const hex = palette.colors[0] || "#CCCCCC";
                      return (
                        <button
                          key={palette.id}
                          type="button"
                          title={palette.label}
                          aria-pressed={isActive}
                          onClick={() => onTogglePalette(palette.id)}
                          className={`flex w-[4.75rem] flex-col items-center gap-1.5 text-left transition-all ${
                            isActive
                              ? "rounded-lg ring-2 ring-secondary ring-offset-2"
                              : isMuted
                                ? "rounded-lg opacity-50 hover:opacity-100 hover:ring-1 hover:ring-secondary"
                                : "rounded-lg hover:ring-1 hover:ring-secondary"
                          }`}
                        >
                          <span
                            className="block h-12 w-12 overflow-hidden rounded-lg border border-light"
                            style={{ backgroundColor: hex }}
                          />
                          <span className="w-full text-center text-xs leading-tight text-secondary">
                            {palette.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {!catalogPalettes.length && !catalogMessage ? (
                    <p className="mt-2 text-sm text-tertiary">
                      No denim palettes in the catalog yet.
                    </p>
                  ) : null}
                </div>

                <div className="flex w-fit shrink-0 flex-nowrap items-start gap-x-3 self-start">
                  <CollectionBuildColorPicker
                    variant="denim"
                    closeKey={`${startingDirection}:${macroTheme}`}
                    onSave={handleSaveCustom}
                  />
                  <div className="flex w-[9.5rem] shrink-0 flex-col items-start">
                    {uploadControl}
                    <p className={`${helperTextClassName} mt-1.5`}>
                      {DENIM_PALETTE_UPLOAD_HELPER}
                    </p>
                  </div>
                </div>
              </div>

              {extraPalettes.length > 0 ? (
                <div className="mt-4 flex min-w-0 max-w-full flex-wrap items-start gap-x-8 gap-y-4">
                  {extraPalettes.map(palette => {
                    const isActive = selectedPaletteIds.includes(palette.id);
                    return (
                      <div key={palette.id} className={paletteColumnClassName}>
                        <button
                          type="button"
                          title={palette.label}
                          aria-pressed={isActive}
                          onClick={() => onTogglePalette(palette.id)}
                          className={paletteChipClassName(isActive)}
                        >
                          <PaletteDots
                            idPrefix={palette.id}
                            colors={palette.colors}
                            swatches={palette.swatches}
                          />
                        </button>
                        <p className={paletteLabelClassName}>{palette.label}</p>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="flex min-w-0 max-w-full flex-wrap items-start gap-x-8 gap-y-4">
                {palettes.map(palette => {
                  const isActive = selectedPaletteIds.includes(palette.id);
                  const capsuleLabel = capsulePaletteDisplayName({
                    startingDirection,
                    capsuleName: capsuleThemeName,
                    paletteId: palette.id,
                    themeId: macroTheme,
                    paletteIsCatalog: catalogPaletteIds.has(palette.id),
                  });
                  const caption = capsuleLabel || palette.label;
                  return (
                    <div key={palette.id} className={paletteColumnClassName}>
                      <button
                        type="button"
                        title={caption}
                        aria-pressed={isActive}
                        onClick={() => onTogglePalette(palette.id)}
                        className={paletteChipClassName(isActive)}
                      >
                        <PaletteDots
                          idPrefix={palette.id}
                          colors={palette.colors}
                          swatches={palette.swatches}
                          dotTitle={capsuleLabel}
                        />
                      </button>
                      <p className={paletteLabelClassName}>{caption}</p>
                    </div>
                  );
                })}

                {!catalogPalettes.length && !catalogMessage ? (
                  <p className="text-sm text-tertiary">
                    No colour stories for this selection yet.
                  </p>
                ) : null}

                <CollectionBuildColorPicker
                  closeKey={`${startingDirection}:${macroTheme}`}
                  onSave={handleSaveCustom}
                />

                <div className="flex max-w-[13.5rem] flex-col items-start self-start">
                  {uploadControl}
                  <p className={`${helperTextClassName} mt-1.5 italic`}>
                    {PALETTE_UPLOAD_HELPER}
                  </p>
                </div>
              </div>
            </>
          )}
          {showPalette && !showMacro && catalogMessage ? (
            <p className={`${helperTextClassName} mt-3`}>{catalogMessage}</p>
          ) : null}
        </SectionBlock>
      )}

      {showTrendSource && !isDenimPath ? (
        <SectionBlock ref={trendSourceRef}>
          <h2 className={`${sectionTitleClassName} mb-3`}>
            Select
            <RequiredMark />
          </h2>
          <ExclusivePills
            options={TREND_SOURCE_CHOICES}
            value={trendSource}
            onChange={onTrendSource}
          />
          {!surfacesLoading &&
          surfaceSlides.length === 0 &&
          trendSource === "surface" ? (
            <p className={`${helperTextClassName} mt-2`}>
              No surface-report slides for these filters. Choose Pattern
              instead.
            </p>
          ) : null}
        </SectionBlock>
      ) : null}

      {showSurfaceSlides && !isDenimPath ? (
        <SectionBlock ref={surfaceRef}>
          <h2 className={`${sectionTitleClassName} mb-4`}>
            Slide Reports
            <RequiredMark />
          </h2>
          <CollectionBuildSlideReports
            slides={surfaceSlides}
            selectedIds={selectedSurfaceIds}
            loading={surfacesLoading}
            onToggle={onToggleSurfaceId}
          />
        </SectionBlock>
      ) : null}

      {showPattern && !isDenimPath && (
        <SectionBlock ref={patternRef}>
          <h2 className={sectionTitleClassName}>
            Select Pattern Trend
            <RequiredMark />
          </h2>
          {isWesternPatternFlow ? (
            <>
              <p className={sectionHelperClassName}>{PATTERN_TREND_HELPER}</p>
              <ExclusivePills
                options={PATTERN_TREND_CHOICES}
                value={patternChoice}
                onChange={onPatternChoice}
              />
              {patternChoice ? (
                <>
                  <p className={`${sectionHelperClassName} mt-4`}>
                    {WESTERN_PATTERN_NARROW_HELPER}
                  </p>
                  <h3 className={`${sectionTitleClassName} mt-4`}>
                    Select Micro-theme
                    <RequiredMark />
                  </h3>
                  {microOptions.length ? (
                    <div className="mt-3">
                      <MicroThemeSelectPills
                        options={microOptions}
                        activeValue={activeMicroTheme}
                        selectedValues={selectedMicroThemes}
                        onSelect={onSelectMicroTheme}
                        groupLabel="Micro-themes"
                      />
                    </div>
                  ) : (
                    <p className={`${helperTextClassName} mt-1`}>
                      No micro-themes in the live catalog.
                    </p>
                  )}
                  {patternTrendReportSlides.length >= 1 ? (
                    <>
                      <button
                        type="button"
                        aria-expanded={showPatternTrendReport}
                        onClick={() => setShowPatternTrendReport(open => !open)}
                        className={viewReportButtonClassName}
                      >
                        {showPatternTrendReport ? "Hide report" : "View report"}
                      </button>
                      {showPatternTrendReport ? (
                        <CollectionBuildReportSlider
                          slides={patternTrendReportSlides}
                        />
                      ) : null}
                    </>
                  ) : null}
                </>
              ) : null}
            </>
          ) : (
            <>
              <p className={sectionHelperClassName}>{PATTERN_TREND_HELPER}</p>
              <ExclusivePills
                options={PATTERN_TREND_CHOICES}
                value={patternChoice}
                onChange={onPatternChoice}
              />
              {patternTrendReportSlides.length >= 1 ? (
                <>
                  <button
                    type="button"
                    aria-expanded={showPatternTrendReport}
                    onClick={() => setShowPatternTrendReport(open => !open)}
                    className={viewReportButtonClassName}
                  >
                    {showPatternTrendReport ? "Hide report" : "View report"}
                  </button>
                  {showPatternTrendReport ? (
                    <CollectionBuildReportSlider
                      slides={patternTrendReportSlides}
                    />
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </SectionBlock>
      )}

      {showPatternStories && !isDenimPath && (
        <SectionBlock>
          <h2 className={sectionTitleClassName}>
            Select Pattern Story
            <RequiredMark />
          </h2>
          {patternStoryOptions.length ? (
            <div className="mt-3">
              <MicroThemeSelectPills
                options={patternStoryOptions}
                activeValue={activePatternStoryId}
                selectedValues={selectedPatternStoryIds}
                onSelect={value => onSelectPatternStory?.(value)}
                groupLabel="Pattern stories"
              />
            </div>
          ) : (
            <p className={`${helperTextClassName} mt-1`}>
              No pattern stories in the live catalog.
            </p>
          )}
        </SectionBlock>
      )}

      {showMicro && !isDenimPath && !isWesternPatternFlow && (
        <SectionBlock ref={microRef}>
          <h2 className={sectionTitleClassName}>
            Select Micro-theme
            <RequiredMark />
          </h2>
          <p className={sectionHelperClassName}>{MICRO_THEME_HELPER}</p>
          {microOptions.length ? (
            <MicroThemeSelectPills
              options={microOptions}
              activeValue={activeMicroTheme}
              selectedValues={selectedMicroThemes}
              onSelect={onSelectMicroTheme}
            />
          ) : (
            <p className={`${helperTextClassName} mt-1`}>
              No micro-themes in the live catalog.
            </p>
          )}
        </SectionBlock>
      )}

      {showPreferences && !isDenimPath && (
        <SectionBlock ref={preferencesRef}>
          <h2 className={`${sectionTitleClassName} mb-1`}>
            Select Preferences{" "}
            <span className="font-normal text-tertiary">(optional)</span>
          </h2>
          <p className={sectionHelperClassName}>{PREFERENCE_SECTION_HELPER}</p>
          <CollectionBuildPreferences
            groups={preferenceGroups}
            selectedIds={selectedPreferenceIds}
            onToggle={onTogglePreference}
            recommended={patternChoice === "recommend"}
          />
        </SectionBlock>
      )}

      {afterPreferences}

      {showMoodboard && (
        <SectionBlock ref={moodboardRef}>
          <h2 className={`${sectionTitleClassName} mb-1`}>
            Enter Input{" "}
            <span className="font-normal text-tertiary">(optional)</span>
          </h2>
          <textarea
            value={additionalPrompt}
            onChange={e => onAdditionalPromptChange(e.target.value)}
            placeholder="Add more blue/ blue green images. The board leans more towards pink and peach themes"
            className={textareaClassName}
            disabled={!showGenerateActions}
            readOnly={!showGenerateActions}
          />
          {showGenerateActions ? (
            <>
              <p className={`${helperTextClassName} mt-6`}>
                {MOODBOARD_HELPER}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={actionsLocked}
                  onClick={onGenerateMoodboard}
                  className={`${mutedButtonClassName} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  Yes, Generate Moodboard
                </button>
                <button
                  type="button"
                  disabled={actionsLocked}
                  onClick={onGenerateLooks}
                  className={`${outlineButtonClassName} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  Proceed to Generate Looks
                </button>
              </div>
            </>
          ) : null}
        </SectionBlock>
      )}
    </>
  );
};

export default CollectionBuildDirections;
